import React, { useEffect, useState } from 'react';
import { ArrowDownToLine, CheckCircle2, Loader2, RefreshCw, ShieldCheck, X } from 'lucide-react';
import { Button } from './ui/button';

const API = process.env.REACT_APP_BACKEND_URL;
const local = typeof window !== 'undefined' && ['127.0.0.1', 'localhost'].includes(window.location.hostname);

export const LocalUpdateNotice = () => {
  const [info, setInfo] = useState(null);
  const [pending, setPending] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!local) return;
    let cancelled = false;
    const load = async () => {
      const token = localStorage.getItem('admin_token');
      if (!token) { setInfo(null); return; }
      try {
        const res = await fetch(`${API}/api/pos/updates/status`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok && !cancelled) setInfo(await res.json());
      } catch { /* Offline/service restart must never interrupt POS. */ }
    };
    load();
    const timer = setInterval(load, 5000); // Local state only, not internet polling.
    return () => { cancelled = true; clearInterval(timer); };
  }, []);
  const version = info?.available_version;
  useEffect(() => { setHidden(false); }, [version]);
  if (!info || !info.enabled) return null;
  const command = async (action) => {
    setPending(true); setError('');
    try {
      const res = await fetch(`${API}/api/pos/updates/${action}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('admin_token')}` }, body: '{}',
      });
      if (res.ok) setInfo(await res.json());
      else setError(res.status === 409 ? 'Идэвхтэй ажиллагаа дууссаны дараа суулгана уу.' : 'Шинэчлэлтийг эхлүүлж чадсангүй. Дараа дахин оролдоно уу.');
    } catch { setError('RENTOS-той холбогдож чадсангүй.'); }
    finally { setPending(false); }
  };
  const show = (version || info.state === 'failed') && !hidden;
  const busy = ['checking', 'downloading', 'verifying', 'installing'].includes(info.state);
  const label = info.reason?.startsWith('installer_') ? 'Суулгалт дуусаагүй. Дахин шалгаж оролдоно уу.' : { checking: 'Шалгаж байна', downloading: 'Татаж байна', verifying: 'Баталгаажуулж байна', installing: 'Windows зөвшөөрөл / суулгалт', ready_to_install: 'Суулгахад бэлэн', blocked_by_active_operation: 'Идэвхтэй ажиллагаа дууссаны дараа суулгана уу.', failed: 'Шинэчлэлтийг баталгаажуулж чадсангүй. Дараа дахин оролдоно уу.' }[info.state];
  if (!show) return info.can_check ? (
    <Button data-testid="local-update-check-button" variant="outline" size="sm" className="fixed bottom-12 sm:bottom-3 right-3 z-50 bg-zinc-900 text-zinc-100 border-zinc-700" onClick={() => { setHidden(false); command('check'); }} disabled={pending || busy}>
      <RefreshCw className="w-3.5 h-3.5 mr-2" />Шинэчлэлт шалгах
    </Button>
  ) : null;
  return (
    <aside data-testid="local-update-notice" aria-live="polite" className="fixed bottom-12 sm:bottom-3 right-3 left-3 sm:left-auto sm:w-96 z-50 border border-emerald-700 bg-zinc-950 text-zinc-100 rounded-lg shadow-xl p-4">
      <div className="flex items-start gap-3">
        <ShieldCheck className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <h2 data-testid="local-update-title" className="text-sm font-semibold text-zinc-100">{version ? 'RENTOS-ийн шинэ хувилбар бэлэн байна' : 'Шинэчлэлтийг баталгаажуулж чадсангүй.'}</h2>
          <p data-testid="local-update-version" className="mt-1 text-xs text-zinc-400 break-all">{info.installed_version}{version && <> → <span className="text-emerald-300">{version}</span></>}</p>
        </div>
        <button data-testid="local-update-dismiss" aria-label="Хаах" title="Хаах" onClick={() => setHidden(true)} className="p-1 text-zinc-400 hover:text-white transition-colors"><X size={16} /></button>
      </div>
      {info.release_notes && <p data-testid="local-update-release-notes" className="text-xs text-zinc-300 mt-3 break-words">{info.release_notes}</p>}
      <p data-testid="local-update-state" className="text-xs text-zinc-300 mt-3 flex gap-2 items-center">{busy ? <Loader2 size={14} className="animate-spin shrink-0" /> : <CheckCircle2 size={14} className="shrink-0" />}{label}</p>
      {info.state === 'downloading' && <progress data-testid="local-update-download-progress" className="w-full h-1 mt-2 accent-emerald-400" value={info.downloaded_bytes} max={info.size_bytes || 1} />}
      {!info.broker_connected && info.state === 'ready_to_install' && <p data-testid="local-update-broker-unavailable" className="text-xs text-amber-300 mt-2">Windows шинэчлэгч холбогдоогүй байна.</p>}
      {error && <p role="alert" data-testid="local-update-error" className="text-xs text-amber-300 mt-2">{error}</p>}
      {info.can_check && <div className="flex flex-wrap gap-2 mt-4">
        <Button data-testid="local-update-install-button" size="sm" className="bg-emerald-500 hover:bg-emerald-400 text-zinc-950" disabled={pending || !info.can_install} onClick={() => command('install')}><ArrowDownToLine className="w-4 h-4 mr-2" />Шинэчлэлт суулгах</Button>
        <Button data-testid="local-update-recheck-button" size="sm" variant="ghost" disabled={pending || busy} onClick={() => command('check')} title="Шинэчлэлт шалгах" aria-label="Шинэчлэлт шалгах"><RefreshCw className="w-4 h-4" /></Button>
      </div>}
    </aside>
  );
};